import React from "react";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";

export const STATUS_META = {
  ricevuto: { label: "Caricati", cls: "bg-slate-100 text-slate-600 hover:bg-slate-100" },
  presa_in_carico: { label: "Presa in carico", cls: "bg-sky-100 text-sky-700 hover:bg-sky-100" },
  in_lavorazione: { label: "In lavorazione", cls: "bg-amber-100 text-amber-700 hover:bg-amber-100" },
  in_prova: { label: "In prova", cls: "bg-indigo-100 text-indigo-700 hover:bg-indigo-100" },
  in_verifica_cliente: { label: "In verifica per cliente", cls: "bg-orange-100 text-orange-800 hover:bg-orange-100 ring-1 ring-orange-300" },
  in_attesa_scomposizione_barra: { label: "In attesa di scomposizione barra", cls: "bg-violet-100 text-violet-700 hover:bg-violet-100" },
  scomposizione_b4d: { label: "Scomposizione B4D", cls: "bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-100" },
  completato: { label: "Completato", cls: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" },
};

export function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.ricevuto;
  return (
    <Badge data-testid="order-status-badge" className={`rounded-full font-semibold border-0 ${meta.cls}`}>
      {meta.label}
    </Badge>
  );
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

// Scadenza con evidenza "Nuova scadenza" e data precedente barrata (dopo aggiornamento cliente)
export function DeadlineCell({ order, className = "" }) {
  const changed = order.previous_deadline && order.previous_deadline.slice(0, 10) !== (order.deadline || "").slice(0, 10);
  if (!changed) return <span className={className}>{formatDate(order.deadline)}</span>;
  return (
    <span className={`inline-flex flex-col leading-tight ${className}`} data-testid="deadline-changed">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Nuova scadenza</span>
      <span className="font-semibold text-amber-800">{formatDate(order.deadline)}</span>
      <span className="text-xs text-slate-400 line-through">{formatDate(order.previous_deadline)}</span>
    </span>
  );
}


export function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

const STEP_LABELS = {
  ricevuto: "Caricati",
  in_lavorazione: "In lavorazione",
  in_prova: "In prova",
  in_attesa_scomposizione_barra: "In attesa scomp. barra",
  scomposizione_b4d: "Scomposizione B4D",
  completato: "Completati",
};

const STANDARD_FLOW = ["ricevuto", "in_lavorazione", "in_prova", "completato"];
const TORONTO_FLOW = ["ricevuto", "in_lavorazione", "in_prova", "in_attesa_scomposizione_barra", "scomposizione_b4d", "completato"];
const SCOMP_STATUSES = ["in_attesa_scomposizione_barra", "scomposizione_b4d"];

export function isTorontoDuo(order) {
  return (order?.design_type || "").toLowerCase().includes("toronto");
}

function flowFor(order) {
  const extended = isTorontoDuo(order) || SCOMP_STATUSES.includes(order?.status);
  return extended ? TORONTO_FLOW : STANDARD_FLOW;
}

function currentIndex(order, steps) {
  let idx = steps.indexOf(order?.status);
  if (idx === -1 && order?.status === "in_verifica_cliente") idx = steps.indexOf("in_prova");
  if (idx === -1) idx = order?.status === "completato" ? steps.length - 1 : 0;
  return idx;
}

export function OrderProgress({ order }) {
  const toronto = isTorontoDuo(order);
  const steps = flowFor(order);
  const current = currentIndex(order, steps);
  const theme = toronto
    ? { done: "bg-violet-600 border-violet-600 text-white", active: "border-violet-600 text-violet-700 ring-violet-100", line: "bg-violet-500", label: "text-violet-700" }
    : { done: "bg-teal-600 border-teal-600 text-white", active: "border-teal-600 text-teal-700 ring-teal-100", line: "bg-teal-500", label: "text-teal-700" };

  return (
    <div data-testid="order-progress">
      {toronto && (
        <span className="inline-block mb-3 rounded-full bg-violet-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-violet-700">
          Toronto Tecnica Duo
        </span>
      )}
      <div className="flex items-start">
        {steps.map((s, idx) => {
          const done = idx < current;
          const isActive = idx === current;
          return (
            <div key={s} className="flex-1 flex flex-col items-center relative">
              {idx > 0 && <div className={`absolute top-4 right-1/2 left-[-50%] h-0.5 ${idx <= current ? theme.line : "bg-slate-200"}`} />}
              <div className={`relative z-10 h-8 w-8 rounded-full border-2 flex items-center justify-center text-xs font-semibold ${done ? theme.done : isActive ? `bg-white ring-4 ${theme.active}` : "bg-white border-slate-300 text-slate-400"}`}>
                {done ? <Check className="h-4 w-4" /> : idx + 1}
              </div>
              <span className={`mt-2 text-[11px] leading-tight text-center px-0.5 ${done || isActive ? `${theme.label} font-medium` : "text-slate-400"}`}>
                {STEP_LABELS[s]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function OrderProgressBar({ order }) {
  const toronto = isTorontoDuo(order);
  const steps = flowFor(order);
  const current = currentIndex(order, steps);
  const pct = steps.length > 1 ? (current / (steps.length - 1)) * 100 : 0;
  const bar = toronto ? "bg-violet-500" : "bg-teal-500";
  const txt = toronto ? "text-violet-600" : "text-teal-600";
  return (
    <div data-testid="order-progress-bar">
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className={`font-medium ${txt}`}>{STEP_LABELS[steps[current]]}</span>
        {toronto && <span className="text-violet-500 font-semibold uppercase tracking-wider">Toronto DUO</span>}
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${bar} transition-all duration-500`} style={{ width: `${Math.max(pct, 6)}%` }} />
      </div>
    </div>
  );
}

export const isStlFile = (name) => /\.stl$/i.test(name || "");
export const isHtmlFile = (name) => /\.html?$/i.test(name || "");

export const FILE_TRACK = {
  operator: { row: "bg-amber-50/70 border-amber-200", dot: "bg-amber-400", label: "Collega" },
  admin: { row: "bg-sky-50/70 border-sky-200", dot: "bg-sky-400", label: "Laboratorio (tu)" },
};

export function fileRowClasses(file) {
  const role = file?.last_downloaded_role;
  return FILE_TRACK[role]?.row || "border-slate-200";
}

export function FileTrackTag({ file }) {
  const role = file?.last_downloaded_role;
  const meta = FILE_TRACK[role];
  if (!meta || !file?.last_downloaded_name) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" data-testid="file-track-tag">
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      Scaricato da {file.last_downloaded_name}
    </span>
  );
}

export function FileTrackLegend() {
  return (
    <div className="flex flex-wrap items-center gap-4 text-[11px] text-slate-500" data-testid="file-track-legend">
      <span className="font-semibold uppercase tracking-wider text-slate-400">Ultimo download:</span>
      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-sky-400" /> Laboratorio (tu)</span>
      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> Collega</span>
      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-slate-200" /> Mai scaricato</span>
    </div>
  );
}
